// Separations: mutual separation agreements for drivers, prepared from the
// driver's own contract and records and checked in four steps by four people.
// db/migrations/5400 to 5406, docs/MUTUAL-SEPARATION.md.
//
// The list puts first the separations waiting on the person looking, so the
// Driver Servant opens on step 1, Yolisa on step 2, the payroll checker on step
// 3 and Luyanda on step 4. A separation starts from an offboarding case whose
// exit type is mutual separation; the list offers those that have none yet.
import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { FilePenLine } from 'lucide-react'
import { useGetSeparations, useOpenSeparation } from '../hooks/backend/separations'
import { useCapabilities } from '../hooks/useCapabilities'
import { Spinner } from '../lib/shadcn/spinner'
import { Button } from '../lib/shadcn/button'
import { toast } from '../lib/shadcn/sonner'
import { cn } from '../lib/shadcn/utils'
import { Chip } from '../components/interviews/Chip'
import { SeparationDetail } from '../components/separations/SeparationDetail'
import { SeparationSummary } from '../components/separations/SeparationSummary'
import { writeErrorMessage } from '../utils/writeError'
import { BASIS_LABEL, STATUS_LABEL, formatDay, type List } from '../utils/separation'

export default function Separations() {
  const { loading: accessLoading, canView } = useCapabilities()
  const list = useGetSeparations<List>()
  const open = useOpenSeparation<{ ok: boolean; id: string }>()
  const load = list.trigger
  const [params, setParams] = useSearchParams()
  const selected = params.get('case')
  const view = params.get('view')
  const [starting, setStarting] = useState<string | null>(null)

  const refresh = useCallback(() => { void load({}, { skipCache: true }).catch(() => undefined) }, [load])
  useEffect(() => { if (canView) refresh() }, [canView, refresh])

  const select = (id: string | null, v: string | null = null) => {
    const next = new URLSearchParams(params)
    if (id) next.set('case', id); else next.delete('case')
    if (v) next.set('view', v); else next.delete('view')
    setParams(next, { replace: true })
  }

  if (accessLoading) {
    return <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground"><Spinner className="h-4 w-4" /> Checking access…</div>
  }
  if (!canView) {
    return <div className="flex h-full items-center justify-center p-6 text-sm text-muted-foreground">Separations are open to Fleetly staff with cockpit access.</div>
  }
  if (selected && view === 'summary') {
    return <SeparationSummary id={selected} onBack={() => select(selected)} />
  }

  const data = list.data
  const cases = data?.cases ?? []
  const mine = cases.filter((c) => c.my_step !== null && ['preparing', 'issued', 'signing'].includes(c.status))
  const rest = cases.filter((c) => !mine.includes(c))

  async function start(offboardingCaseId: string) {
    setStarting(offboardingCaseId)
    try {
      const r = await open.trigger({ offboarding_case_id: offboardingCaseId }).result
      toast.success('Separation opened. Step 1 is with the Driver Servant.')
      refresh()
      select(r.id)
    } catch (e) {
      toast.error(writeErrorMessage(e))
    } finally {
      setStarting(null)
    }
  }

  const row = (c: List['cases'][number]) => (
    <button key={c.id} type="button" onClick={() => select(c.id)}
      className={cn('w-full rounded-md border border-border p-2.5 text-left hover:bg-muted/50', selected === c.id && 'border-[hsl(var(--brand))] bg-muted/40')}>
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-sm font-medium text-foreground">{c.driver_name || 'Driver'}</span>
        <span className="text-xs text-muted-foreground">{c.case_ref}</span>
      </div>
      <div className="mt-1 flex flex-wrap gap-1">
        <Chip tone={c.status === 'signed' ? 'green' : ['declined', 'cancelled'].includes(c.status) ? 'neutral' : 'amber'}>{STATUS_LABEL[c.status]}</Chip>
        <Chip tone={c.basis === 'manual_review' ? 'red' : 'neutral'}>{BASIS_LABEL[c.basis]}</Chip>
        {c.my_step !== null && <Chip tone="brand">Your step {c.my_step}</Chip>}
      </div>
      <p className="mt-1 text-[11px] text-muted-foreground">
        Ends {formatDay(c.termination_date)} · {c.offboarding_case_ref} · steps{' '}
        {c.steps.map((s) => (s.status === 'approved' ? `${s.step} done` : `${s.step} ${s.assignee ?? ''}`.trim())).join(', ')}
      </p>
    </button>
  )

  return (
    <div className="flex h-full min-h-0 flex-col md:flex-row">
      <aside className={cn('w-full shrink-0 space-y-3 overflow-y-auto border-border p-3 md:w-80 md:border-r', selected && 'hidden md:block')}>
        <div className="flex items-center gap-2">
          <FilePenLine className="h-4 w-4 text-[hsl(var(--brand))]" />
          <h1 className="text-base font-semibold text-foreground">Separations</h1>
        </div>
        <p className="text-xs text-muted-foreground">
          A driver facing an allegation may ask to leave by agreement instead of a hearing. The agreement is built from the
          driver's own contract and records, given to the driver unsigned for at least two working days, and checked by four
          different people before anyone signs.
        </p>
        {!data && <p className="flex items-center gap-2 text-xs text-muted-foreground"><Spinner className="h-3 w-3" /> Loading…</p>}
        {list.error && !data && <p className="text-xs text-rose-700 dark:text-rose-300">{list.error}</p>}
        {data && data.can_start.length > 0 && (
          <section className="space-y-1.5 rounded-md border border-dashed border-border p-2">
            <h2 className="text-xs font-semibold text-foreground">Offboarding cases marked mutual separation</h2>
            {data.can_start.map((s) => (
              <div key={s.offboarding_case_id} className="flex items-center justify-between gap-2">
                <span className="text-xs">{s.driver_name} · {s.case_ref}</span>
                <Button size="sm" variant="outline" disabled={!data.access.can_open || starting !== null} onClick={() => void start(s.offboarding_case_id)}>
                  {starting === s.offboarding_case_id ? <Spinner className="h-3 w-3" /> : 'Start'}
                </Button>
              </div>
            ))}
            {!data.access.can_open && <p className="text-[11px] text-muted-foreground">Starting one needs Edit drivers, an offboarding signatory permission or the Driver Servant role.</p>}
          </section>
        )}
        {data && cases.length === 0 && data.can_start.length === 0 && (
          <p className="text-xs text-muted-foreground">
            No separations. One starts from an offboarding case: open the case from the driver record with the exit type
            "Mutual separation", and it appears here.
          </p>
        )}
        {mine.length > 0 && <h2 className="pt-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Waiting for you</h2>}
        {mine.map(row)}
        {rest.length > 0 && <h2 className="pt-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">All</h2>}
        {rest.map(row)}
      </aside>
      <main className="min-h-0 flex-1">
        {selected ? (
          <SeparationDetail id={selected} onBack={() => select(null)} onSummary={() => select(selected, 'summary')} onChanged={refresh} />
        ) : (
          <div className="hidden h-full items-center justify-center p-6 text-sm text-muted-foreground md:flex">Choose a separation.</div>
        )}
      </main>
    </div>
  )
}
